import { useEffect, useState } from "react";
import { toast } from "sonner";
import MobileShell from "@/components/admin/MobileShell";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, Save } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: ss }, { data: rs }, { data: fs }] = await Promise.all([
      api.get("/admin/settings"), api.get("/admin/reviews"), api.get("/admin/faqs"),
    ]);
    setS(ss); setReviews(rs); setFaqs(fs);
  };
  useEffect(() => { load(); }, []);

  if (!s) return <MobileShell title="Settings"><div className="text-slate-500 text-sm">Loading…</div></MobileShell>;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", patch);
      setS(data);
      toast.success("Settings saved.");
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const toggleDay = (day) => {
    const next = s.appointment_days.includes(day) ? s.appointment_days.filter((d) => d !== day) : [...s.appointment_days, day];
    save({ appointment_days: next });
  };

  return (
    <MobileShell title="Settings">
      <h1 className="font-display text-2xl text-[#0A1930] tracking-tight mt-2">Settings</h1>
      <p className="text-sm text-slate-500 mt-1 mb-4">Tune the entire business from your phone.</p>

      <Card title="Compensation" testid="settings-fee">
        <Row label="Success fee %">
          <Input type="number" value={s.fee_percentage} min={0} max={100}
            onChange={(e) => setS({ ...s, fee_percentage: Number(e.target.value) })}
            onBlur={(e) => save({ fee_percentage: Number(e.target.value) })}
            className="w-28 h-11" data-testid="settings-fee-input" />
        </Row>
        <Row label="Contract expires in (days)">
          <Input type="number" value={s.contract_expiry_days} min={1} max={30}
            onChange={(e) => setS({ ...s, contract_expiry_days: Number(e.target.value) })}
            onBlur={(e) => save({ contract_expiry_days: Number(e.target.value) })}
            className="w-28 h-11" />
        </Row>
      </Card>

      <Card title="Appointment availability" testid="settings-hours">
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-[0.12em] text-slate-500">Days</Label>
          <div className="flex flex-wrap gap-2" data-testid="settings-days">
            {DAYS.map((d) => {
              const on = s.appointment_days.includes(d);
              return (
                <button key={d} onClick={() => toggleDay(d)}
                  className={`px-3.5 py-1.5 rounded-full text-sm border ${on ? "bg-[#0A1930] text-white border-[#0A1930]" : "bg-white text-[#0A1930] border-slate-200"}`}
                  data-testid={`day-${d}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Row label="Start"><Input type="time" value={s.appointment_start} onChange={(e) => setS({ ...s, appointment_start: e.target.value })} onBlur={(e) => save({ appointment_start: e.target.value })} className="h-11" /></Row>
          <Row label="End"><Input type="time" value={s.appointment_end} onChange={(e) => setS({ ...s, appointment_end: e.target.value })} onBlur={(e) => save({ appointment_end: e.target.value })} className="h-11" /></Row>
          <Row label="Slot (min)"><Input type="number" value={s.slot_minutes} onChange={(e) => setS({ ...s, slot_minutes: Number(e.target.value) })} onBlur={(e) => save({ slot_minutes: Number(e.target.value) })} className="h-11" /></Row>
        </div>
      </Card>

      <Card title="Homepage" testid="settings-homepage">
        <Row label="Headline"><Input value={s.homepage_headline} onChange={(e) => setS({ ...s, homepage_headline: e.target.value })} onBlur={(e) => save({ homepage_headline: e.target.value })} className="h-11" /></Row>
        <Row label="Sub-headline"><Textarea rows={3} value={s.homepage_subheadline} onChange={(e) => setS({ ...s, homepage_subheadline: e.target.value })} onBlur={(e) => save({ homepage_subheadline: e.target.value })} /></Row>
      </Card>

      <Card title="Contract disclaimer" testid="settings-disclaimer">
        <Textarea rows={4} value={s.disclaimer} onChange={(e) => setS({ ...s, disclaimer: e.target.value })} onBlur={(e) => save({ disclaimer: e.target.value })} />
      </Card>

      <Card title="Email & SMS templates" testid="settings-templates">
        <Row label="Booking email (client)"><Textarea rows={5} value={s.email_template_booking_client} onChange={(e) => setS({ ...s, email_template_booking_client: e.target.value })} onBlur={(e) => save({ email_template_booking_client: e.target.value })} /></Row>
        <Row label="Contract email"><Textarea rows={5} value={s.email_template_contract} onChange={(e) => setS({ ...s, email_template_contract: e.target.value })} onBlur={(e) => save({ email_template_contract: e.target.value })} /></Row>
        <Row label="Booking SMS (client)"><Textarea rows={2} value={s.sms_template_booking_client} onChange={(e) => setS({ ...s, sms_template_booking_client: e.target.value })} onBlur={(e) => save({ sms_template_booking_client: e.target.value })} /></Row>
        <Row label="Contract SMS"><Textarea rows={2} value={s.sms_template_contract} onChange={(e) => setS({ ...s, sms_template_contract: e.target.value })} onBlur={(e) => save({ sms_template_contract: e.target.value })} /></Row>
        <Row label="Reminder SMS (client)"><Textarea rows={2} value={s.sms_template_reminder_client || ""} onChange={(e) => setS({ ...s, sms_template_reminder_client: e.target.value })} onBlur={(e) => save({ sms_template_reminder_client: e.target.value })} /></Row>
        <Row label="Admin booking alert SMS"><Textarea rows={2} value={s.sms_template_admin_booking || ""} onChange={(e) => setS({ ...s, sms_template_admin_booking: e.target.value })} onBlur={(e) => save({ sms_template_admin_booking: e.target.value })} /></Row>
        <Row label="Admin reminder SMS"><Textarea rows={2} value={s.sms_template_admin_reminder || ""} onChange={(e) => setS({ ...s, sms_template_admin_reminder: e.target.value })} onBlur={(e) => save({ sms_template_admin_reminder: e.target.value })} /></Row>
        <p className="text-[11px] text-slate-400 leading-relaxed">
          Available placeholders: <code>{`{company}`}</code>, <code>{`{name}`}</code>, <code>{`{branch}`}</code>, <code>{`{datetime}`}</code>, <code>{`{time}`}</code>, <code>{`{when}`}</code>, <code>{`{phone}`}</code>, <code>{`{link}`}</code>
        </p>
      </Card>

      <AdminPhonesCard s={s} save={save} />

      <RemindersCard s={s} save={save} />

      <ReviewsEditor reviews={reviews} reload={load} />
      <FaqsEditor faqs={faqs} reload={load} />
    </MobileShell>
  );
}

function AdminPhonesCard({ s, save }) {
  const [draft, setDraft] = useState("");
  const phones = s.admin_notify_phones || [];
  const add = () => {
    const v = (draft || "").trim();
    if (!v) return;
    if (phones.includes(v)) { toast.info("Already added."); return; }
    save({ admin_notify_phones: [...phones, v] });
    setDraft("");
  };
  const remove = (p) => save({ admin_notify_phones: phones.filter((x) => x !== p) });
  return (
    <Card title="Admin notification phones" testid="settings-admin-phones">
      <p className="text-xs text-slate-500 -mt-1">Everyone here gets a text on new bookings and upcoming-appointment reminders.</p>
      <div className="space-y-2">
        {phones.length === 0 && <p className="text-sm text-slate-400">No admin phones yet — add one below.</p>}
        {phones.map((p) => (
          <div key={p} className="flex items-center justify-between rounded-2xl border border-slate-200 px-3 py-2" data-testid={`admin-phone-row-${p}`}>
            <span className="text-sm text-[#0A1930]">{p}</span>
            <button onClick={() => remove(p)} className="text-slate-400 hover:text-red-500 text-xs" data-testid={`admin-phone-remove-${p}`}>Remove</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="+13853547742" className="h-11" data-testid="admin-phone-input" />
        <Button onClick={add} className="h-11 rounded-full" data-testid="admin-phone-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">Tip: enter in E.164 format like <code>+15551234567</code>. Local 10-digit numbers are auto-normalized.</p>
    </Card>
  );
}

function offsetsListToString(arr) { return Array.isArray(arr) ? arr.join(", ") : ""; }
function stringToOffsets(s) {
  return (s || "")
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function RemindersCard({ s, save }) {
  const [client, setClient] = useState(offsetsListToString(s.client_reminder_offsets_hours));
  const [admin, setAdmin] = useState(offsetsListToString(s.admin_reminder_offsets_hours));
  const [running, setRunning] = useState(false);
  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/reminders/run");
      toast.success("Reminder check ran.");
    } catch { toast.error("Failed to run."); } finally { setRunning(false); }
  };
  return (
    <Card title="Reminders" testid="settings-reminders">
      <p className="text-xs text-slate-500 -mt-1">Comma-separated hours before each appointment. Example: <code>24, 2</code></p>
      <Row label="Client reminder offsets (hours before)">
        <Input value={client}
          onChange={(e) => setClient(e.target.value)}
          onBlur={() => save({ client_reminder_offsets_hours: stringToOffsets(client) })}
          className="h-11" data-testid="client-offsets-input" />
      </Row>
      <Row label="Admin reminder offsets (hours before)">
        <Input value={admin}
          onChange={(e) => setAdmin(e.target.value)}
          onBlur={() => save({ admin_reminder_offsets_hours: stringToOffsets(admin) })}
          className="h-11" data-testid="admin-offsets-input" />
      </Row>
      <Button onClick={runNow} disabled={running} variant="outline" className="h-11 rounded-full" data-testid="reminders-run-now">
        {running ? "Running…" : "Run reminder check now"}
      </Button>
      <p className="text-[11px] text-slate-400">The scheduler also runs automatically every 5 minutes.</p>
    </Card>
  );
}

function Card({ title, children, testid }) {
  return (
    <section className="mt-4 bg-white border border-slate-200 rounded-3xl p-5" data-testid={testid}>
      <h2 className="text-[11px] tracking-[0.18em] uppercase text-slate-500">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReviewsEditor({ reviews, reload }) {
  const [draft, setDraft] = useState({ name: "", branch: "", rating: 5, body: "", published: true });

  const add = async () => {
    if (!draft.name || !draft.body) return toast.error("Name and body required.");
    await api.post("/admin/reviews", draft);
    setDraft({ name: "", branch: "", rating: 5, body: "", published: true });
    reload();
    toast.success("Review added.");
  };
  const update = async (r, patch) => { await api.patch(`/admin/reviews/${r.review_id}`, { ...r, ...patch }); reload(); };
  const remove = async (r) => { await api.delete(`/admin/reviews/${r.review_id}`); reload(); toast.success("Removed."); };

  return (
    <Card title="Reviews" testid="settings-reviews">
      <div className="space-y-2">
        {reviews.map((r) => (
          <div key={r.review_id} className="rounded-2xl border border-slate-200 p-3" data-testid={`review-row-${r.review_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0A1930] truncate">{r.name} · {r.branch}</p>
                <p className="text-xs text-slate-500 truncate">{r.body}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={r.published} onCheckedChange={(v) => update(r, { published: v })} />
                <button onClick={() => remove(r)} className="text-slate-400 hover:text-red-500" data-testid={`review-delete-${r.review_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-2xl border border-dashed border-slate-200 p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-11" data-testid="review-name" />
          <Input placeholder="Branch" value={draft.branch} onChange={(e) => setDraft({ ...draft, branch: e.target.value })} className="h-11" data-testid="review-branch" />
        </div>
        <Textarea placeholder="Review body" rows={3} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="review-body" />
        <div className="flex justify-between items-center">
          <Input type="number" min={1} max={5} value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })} className="w-20 h-11" />
          <Button onClick={add} className="h-11 rounded-full" data-testid="review-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </div>
    </Card>
  );
}

function FaqsEditor({ faqs, reload }) {
  const [draft, setDraft] = useState({ question: "", answer: "", order: 0 });
  const add = async () => {
    if (!draft.question || !draft.answer) return toast.error("Question and answer required.");
    await api.post("/admin/faqs", { ...draft, order: faqs.length + 1 });
    setDraft({ question: "", answer: "", order: 0 });
    reload();
  };
  const update = async (f, patch) => { await api.patch(`/admin/faqs/${f.faq_id}`, { ...f, ...patch }); reload(); };
  const remove = async (f) => { await api.delete(`/admin/faqs/${f.faq_id}`); reload(); };

  return (
    <Card title="FAQ" testid="settings-faqs">
      <div className="space-y-2">
        {faqs.map((f) => (
          <details key={f.faq_id} className="rounded-2xl border border-slate-200 p-3" data-testid={`faq-row-${f.faq_id}`}>
            <summary className="text-sm font-medium text-[#0A1930] cursor-pointer">{f.question}</summary>
            <div className="mt-2 space-y-2">
              <Input value={f.question} onChange={(e) => update(f, { question: e.target.value })} className="h-11" />
              <Textarea value={f.answer} onChange={(e) => update(f, { answer: e.target.value })} rows={3} />
              <button onClick={() => remove(f)} className="text-xs text-red-500 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
            </div>
          </details>
        ))}
      </div>
      <div className="mt-3 rounded-2xl border border-dashed border-slate-200 p-3 space-y-2">
        <Input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} className="h-11" data-testid="faq-q" />
        <Textarea placeholder="Answer" rows={3} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} data-testid="faq-a" />
        <Button onClick={add} className="h-11 rounded-full" data-testid="faq-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
    </Card>
  );
}
